import { useRef, useState } from "react";
import { Eye, FileText, Loader, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import styles from "./FileUploadInput.module.css";

interface FileUploadInputProps {
  label: string;
  acceptedMimeTypes: string[];
  acceptedFormatsLabel: string[];
  maxSizeMb: number;
  currentUrl?: string;
  uploadFn: (file: File) => Promise<string>;
  onUpload: (url: string) => void;
  onRemove: () => void;
  onPreview?: () => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export function FileUploadInput({
  label,
  acceptedMimeTypes,
  acceptedFormatsLabel,
  maxSizeMb,
  currentUrl,
  uploadFn,
  onUpload,
  onRemove,
  onPreview,
  onError,
}: FileUploadInputProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    if (!acceptedMimeTypes.includes(file.type)) {
      setError(
        t("fileUpload.invalidFormat", {
          formats: acceptedFormatsLabel.join(", "),
        }),
      );
      e.target.value = "";
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(t("fileUpload.tooLarge", { max: maxSizeMb }));
      e.target.value = "";
      return;
    }

    setIsUploading(true);
    try {
      const url = await uploadFn(file);
      onUpload(url);
    } catch (err) {
      if (onError) {
        onError(err);
      } else {
        setError(t("fileUpload.error"));
      }
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const fileName = currentUrl ? currentUrl.split("/").pop() : null;

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{label}</span>
      <span className={styles.hint}>
        {acceptedFormatsLabel.join(", ")} — max {maxSizeMb} Mo
      </span>

      {currentUrl ? (
        <div className={styles.fileRow}>
          <FileText size={16} />
          <span className={styles.fileLink}>{fileName}</span>
          {onPreview && (
            <button
              type="button"
              className={styles.previewBtn}
              onClick={onPreview}
            >
              <Eye size={14} />
            </button>
          )}
          <button type="button" className={styles.removeBtn} onClick={onRemove}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.uploadBtn}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <Loader size={16} className={styles.spin} />{" "}
              {t("fileUpload.uploading")}
            </>
          ) : (
            <>
              <Upload size={16} /> {t("fileUpload.choose")}
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptedMimeTypes.join(",")}
        onChange={handleChange}
        style={{ display: "none" }}
      />

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
